import { DOCUMENT_PAGE_RENDERING_CANCELLED_EXCEPTION } from 'constants/documents';
import { openURLInCurrentTab, openURLInNewTab } from 'utils/navigation';

const getPdfData = pdf =>
  new Promise((resolve, reject) => {
    if (pdf) {
      pdf.getData().then(async pdfData => {
        const { PDFDocument } = await import('pdf-lib');

        PDFDocument.load(pdfData, { ignoreEncryption: true }).then(pdfLibDoc => {
          if (pdfLibDoc.isEncrypted) {
            reject('Selected document is encrypted and cannot be edited!');
          }

          resolve(pdfLibDoc);
        });
      });
    }
  });

export const mergePdfs = async pdfsToMerge => {
  const { PDFDocument } = await import('pdf-lib');
  const mergedPdf = await PDFDocument.create();

  for (const pdfCopyDoc of pdfsToMerge) {
    const pdfData = await getPdfData(pdfCopyDoc);
    const copiedPages = await mergedPdf.copyPages(pdfData, pdfData.getPageIndices());
    copiedPages?.forEach(page => {
      mergedPdf.addPage(page);
    });
  }

  return mergedPdf.save();
};

// The legacy build polyfills APIs the modern pdf.js 6 build needs natively (Chrome 145+ / Safari 26.2+),
// so documents still open in older browsers and the iOS WebView. Its worker, the standard-14 fonts and
// the wasm image decoders (JPEG 2000, JBIG2) are copied into the build and load from the app's origin.
const setPdfJsWorker = async () => {
  const PdfJs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  PdfJs.GlobalWorkerOptions.workerSrc = process.env.PDFJS_WORKER_URL;

  return PdfJs;
};

// With no cMapUrl set, pdf.js fetches the fonts on the main thread.
export const getPdfJsDocument = async params => {
  const PdfJs = await setPdfJsWorker();

  return PdfJs.getDocument({
    ...params,
    standardFontDataUrl: process.env.PDFJS_STANDARD_FONTS_URL,
    wasmUrl: process.env.PDFJS_WASM_URL,
  });
};

export const checkIsPageRenderingCancelledException = err =>
  err?.name === DOCUMENT_PAGE_RENDERING_CANCELLED_EXCEPTION;

export const getPdfBlobFromBytes = encodedData => {
  const decodedData = atob(encodedData);
  const bytesData = new Uint8Array(decodedData.length);

  for (let i = 0; i < decodedData.length; i++) {
    bytesData[i] = decodedData.charCodeAt(i);
  }

  return new Blob([bytesData], { type: 'application/pdf' });
};

export const openPdfFromBytes = (encodedData, options = {}) => {
  const { shouldFocusTab = true, openInNewTab = true } = options;

  const blob = getPdfBlobFromBytes(encodedData);
  const fileURL = URL.createObjectURL(blob);

  if (openInNewTab) {
    return openURLInNewTab(fileURL, shouldFocusTab);
  }

  openURLInCurrentTab(fileURL);
};
